import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { api, ApiError } from '../../api';
import { store } from '../../store';
import { showToast } from '../../components/ui/toast';
import { icon } from '../../components/ui/icons';
import { t } from '../../i18n';

interface EventFile { id: string; filename: string; file_size: number | null; mime_type: string | null; uploader_name: string | null; created_at: string; }

const MAX_BYTES = 25 * 1024 * 1024;

function fmtSize(bytes: number | null): string {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * <cadence-event-files> — the Files tab for an event. Drag-and-drop or click to upload
 * attachments (stored in R2), listed with size/uploader and a permission-gated download
 * link. Read for events.view; upload/delete gated by events.manage_notes.
 */
@customElement('cadence-event-files')
export class CadenceEventFiles extends LitElement {
  @property() eventId = '';
  @state() private files: EventFile[] = [];
  @state() private loading = true;
  @state() private uploading = false;
  @state() private dragOver = false;
  @query('input[type=file]') private fileInput!: HTMLInputElement;

  private readonly canManage = store.hasPermission('events.manage_notes');

  static styles = css`
    :host { display: block; }
    input[type=file] { display: none; }
    .drop { border: 2px dashed var(--color-border); border-radius: var(--radius-md); padding: var(--space-5); text-align: center; color: var(--color-text-secondary); cursor: pointer; margin-bottom: var(--space-4); transition: border-color 0.15s, background 0.15s; }
    .drop:hover, .drop.over { border-color: var(--color-primary); background: var(--color-bg-secondary); }
    .drop svg { width: 28px; height: 28px; margin-bottom: 6px; }
    .drop .hint { font-size: 0.78rem; }
    .item { display: flex; align-items: center; gap: 12px; padding: 10px 0; border-bottom: 1px solid var(--color-border); }
    .item:last-child { border-bottom: none; }
    .item .ic { color: var(--color-text-secondary); display: inline-flex; flex-shrink: 0; }
    .item .ic svg { width: 22px; height: 22px; }
    .item .info { flex: 1; min-width: 0; }
    .item .name { font-weight: 600; word-break: break-word; }
    .item .meta { color: var(--color-text-secondary); font-size: 0.78rem; }
    .item .acts { display: flex; gap: 4px; align-items: center; flex-shrink: 0; }
    .iconbtn { background: none; border: none; cursor: pointer; color: var(--color-text-secondary); padding: 6px; border-radius: 6px; display: inline-flex; align-items: center; text-decoration: none; }
    .iconbtn svg { width: 18px; height: 18px; }
    .iconbtn:hover { background: var(--color-bg-secondary); color: var(--color-primary); }
    .iconbtn.del:hover { color: var(--color-danger); }
  `;

  connectedCallback(): void { super.connectedCallback(); if (this.eventId) this.load(); }
  updated(c: Map<string, unknown>) { if (c.has('eventId') && this.eventId) this.load(); }

  private async load() {
    this.loading = true;
    try { this.files = await api.get<EventFile[]>(`/events/${this.eventId}/files`); }
    catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private async uploadFile(file: File) {
    if (file.size > MAX_BYTES) { showToast(t('events.toast.fileTooLarge'), 'error'); return; }
    this.uploading = true;
    try {
      await api.upload(`/events/${this.eventId}/files?filename=${encodeURIComponent(file.name)}`, file);
      await this.load();
      showToast(t('events.toast.fileUploaded'), 'success');
    } catch (e) { showToast(e instanceof ApiError ? e.message : t('events.toast.uploadFailed'), 'error'); }
    finally { this.uploading = false; if (this.fileInput) this.fileInput.value = ''; }
  }

  private onPick(e: Event) {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) this.uploadFile(file);
  }
  private onDrop(e: DragEvent) {
    e.preventDefault(); this.dragOver = false;
    const file = e.dataTransfer?.files?.[0];
    if (file) this.uploadFile(file);
  }

  private async del(f: EventFile) {
    if (!confirm(t('events.files.confirmDelete', { name: f.filename }))) return;
    try { await api.del(`/events/${this.eventId}/files/${f.id}`); await this.load(); }
    catch (e) { showToast((e as Error).message, 'error'); }
  }

  render() {
    if (this.loading) return html`<cadence-loading></cadence-loading>`;
    return html`
      ${this.canManage ? html`
        <div class="drop ${this.dragOver ? 'over' : ''}"
          @click=${() => this.fileInput?.click()}
          @dragover=${(e: DragEvent) => { e.preventDefault(); this.dragOver = true; }}
          @dragleave=${() => (this.dragOver = false)}
          @drop=${(e: DragEvent) => this.onDrop(e)}>
          ${icon('upload', 28)}
          <div>${this.uploading ? t('events.files.uploading') : t('events.files.dropPrompt')}</div>
          <div class="hint">${t('events.files.hint')}</div>
        </div>
        <input type="file" @change=${this.onPick} />` : ''}

      ${this.files.length
        ? this.files.map((f) => html`
          <div class="item">
            <span class="ic">${icon('file', 22)}</span>
            <div class="info">
              <div class="name">${f.filename}</div>
              <div class="meta">${fmtSize(f.file_size)}${f.uploader_name ? ' · ' + f.uploader_name : ''} · ${(f.created_at ?? '').slice(0, 10)}</div>
            </div>
            <div class="acts">
              <a class="iconbtn" title=${t('events.files.download')} href=${`/api/v1/events/${this.eventId}/files/${f.id}/download`} download=${f.filename}>${icon('download')}</a>
              ${this.canManage ? html`<button class="iconbtn del" title=${t('events.actions.delete')} @click=${() => this.del(f)}>${icon('trash-2')}</button>` : ''}
            </div>
          </div>`)
        : html`<cadence-empty-state heading=${t('events.files.empty.heading')} message=${this.canManage ? t('events.files.empty.manage') : t('common.states.empty')}></cadence-empty-state>`}
    `;
  }
}
